'use client';

import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { LoadingButton } from '../../../components/ui/loading-button';
import { Textarea } from '../../../components/ui/textarea';
import type { GameMetadata } from '@/types/game';
import { gamesApi } from '@/lib/api/games';
interface CreateGameDialogProps {
  loading: boolean;
  onSuccess: () => void;
  onError: (error: string) => void;
  className?: string;
}
export function CreateGameDialog({
  loading,
  onSuccess,
  onError,
  className
}: CreateGameDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [newGameMetadata, setNewGameMetadata] = useState<GameMetadata>({
    name: '',
    description: '',
    tags: [],
    thumbnailUrl: ''
  });
  const [newTag, setNewTag] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setNewGameMetadata({
        name: '',
        description: '',
        tags: [],
        thumbnailUrl: ''
      });
      setNewTag('');
    }
  }, [isOpen]);
  const handleCreateGame = async () => {
    // Validate metadata
    if (!newGameMetadata.name.trim()) {
      onError('Game name is required');
      return;
    }
    try {
      setIsCreating(true);
      await gamesApi.create({
        metadata: newGameMetadata
      });

      // Close dialog
      setIsOpen(false);
      onSuccess();
    } catch (err) {
      console.error('Error creating game:', err);
      onError('Failed to create game. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };
  const addTag = () => {
    if (newTag.trim() && !newGameMetadata.tags.includes(newTag.trim())) {
      setNewGameMetadata({
        ...newGameMetadata,
        tags: [...newGameMetadata.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };
  const removeTag = (tagToRemove: string) => {
    setNewGameMetadata({
      ...newGameMetadata,
      tags: newGameMetadata.tags.filter(tag => tag !== tagToRemove)
    });
  };
  return <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button disabled={loading} className={`w-full ${className}`}>
          Create a New Game
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] ">
        <DialogHeader>
          <DialogTitle>Create New Game</DialogTitle>
          <DialogDescription>
            Fill out the game metadata details below.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-6">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">
              Name*
            </Label>
            <Input id="name" value={newGameMetadata.name} onChange={e => setNewGameMetadata({
            ...newGameMetadata,
            name: e.target.value
          })} className="col-span-3" placeholder="Game name" required />
          </div>
          <div className="grid grid-cols-4 items-start gap-4">
            <Label htmlFor="description" className="text-right pt-2">
              Description
            </Label>
            <Textarea id="description" value={newGameMetadata.description} onChange={e_0 => setNewGameMetadata({
            ...newGameMetadata,
            description: e_0.target.value
          })} className="col-span-3" placeholder="Game description" rows={6} />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="tags" className="text-right">
              Tags
            </Label>
            <div className="col-span-3 space-y-2">
              <div className="flex gap-2">
                <Input id="tags" value={newTag} onChange={e_1 => setNewTag(e_1.target.value)} placeholder="Add a tag" onKeyDown={(e_2: React.KeyboardEvent<HTMLInputElement>) => {
                if (e_2.key === 'Enter') {
                  e_2.preventDefault();
                  addTag();
                }
              }} />
                <Button type="button" onClick={addTag} disabled={isCreating}>
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {newGameMetadata.tags.map(tag_0 => <Badge key={tag_0} variant="outline">
                    {tag_0}
                    <button onClick={() => removeTag(tag_0)} className="rounded-full hover:bg-muted p-1">
                      <X className="h-4 w-4" />
                    </button>
                  </Badge>)}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <LoadingButton type="submit" onClick={handleCreateGame} isLoading={isCreating} loadingText="Creating..." disabled={loading}>
            Create Game
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}